/**
 * Audio API Hooks
 *
 * TanStack Query hooks for audio file operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAudioFilesApiV1DatasetsDatasetIdAudioGet,
  getAudioFileApiV1DatasetsDatasetIdAudioAudioIdGet,
  deleteAudioFileApiV1DatasetsDatasetIdAudioAudioIdDelete,
  getUploadUrlApiV1DatasetsDatasetIdAudioUploadUrlPost,
  confirmUploadApiV1DatasetsDatasetIdAudioConfirmPost,
  getDownloadUrlApiV1DatasetsDatasetIdAudioAudioIdDownloadUrlGet,
} from '@/api';
import type { UploadUrlRequest } from '@/api';
import { datasetKeys } from './datasets';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const audioKeys = {
  all: ['audio'] as const,
  lists: () => [...audioKeys.all, 'list'] as const,
  list: (datasetId: string) => [...audioKeys.lists(), datasetId] as const,
  details: () => [...audioKeys.all, 'detail'] as const,
  detail: (datasetId: string, audioId: string) =>
    [...audioKeys.details(), datasetId, audioId] as const,
};

/**
 * List audio files for a dataset.
 */
export function useAudioFiles(datasetId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(datasetId));

  return useQuery({
    queryKey: audioKeys.list(datasetId),
    queryFn: async () => {
      const response = await listAudioFilesApiV1DatasetsDatasetIdAudioGet({
        path: { dataset_id: datasetId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch audio files'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Get a single audio file.
 */
export function useAudioFile(datasetId: string, audioId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(datasetId) && Boolean(audioId));

  return useQuery({
    queryKey: audioKeys.detail(datasetId, audioId),
    queryFn: async () => {
      const response = await getAudioFileApiV1DatasetsDatasetIdAudioAudioIdGet({
        path: { dataset_id: datasetId, audio_id: audioId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch audio file'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Delete an audio file.
 */
export function useDeleteAudioFile(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (audioId: string) => {
      const response =
        await deleteAudioFileApiV1DatasetsDatasetIdAudioAudioIdDelete({
          path: { dataset_id: datasetId, audio_id: audioId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to delete audio file'
        );
      }
      return response.data;
    },
    onSuccess: (_, audioId) => {
      queryClient.removeQueries({
        queryKey: audioKeys.detail(datasetId, audioId),
      });
      queryClient.invalidateQueries({ queryKey: audioKeys.list(datasetId) });
      queryClient.invalidateQueries({
        queryKey: datasetKeys.detail(datasetId),
      });
    },
  });
}

/**
 * Get presigned upload URL for direct S3 upload.
 */
export function useGetUploadUrl(datasetId: string) {
  return useMutation({
    mutationFn: async (data: UploadUrlRequest) => {
      const response =
        await getUploadUrlApiV1DatasetsDatasetIdAudioUploadUrlPost({
          path: { dataset_id: datasetId },
          body: data,
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to get upload URL'
        );
      }
      return response.data;
    },
  });
}

/**
 * Confirm upload after S3 upload completes.
 */
export function useConfirmUpload(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (audioId: string) => {
      const response =
        await confirmUploadApiV1DatasetsDatasetIdAudioConfirmPost({
          path: { dataset_id: datasetId },
          body: { audio_id: audioId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to confirm upload'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: audioKeys.list(datasetId) });
      queryClient.invalidateQueries({
        queryKey: datasetKeys.detail(datasetId),
      });
    },
  });
}

/**
 * Get download URL for an audio file (query-based, auto-fetches).
 */
export function useGetDownloadUrl(datasetId: string, audioId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(datasetId) && Boolean(audioId));

  return useQuery({
    queryKey: [...audioKeys.detail(datasetId, audioId), 'download-url'],
    queryFn: async () => {
      const response =
        await getDownloadUrlApiV1DatasetsDatasetIdAudioAudioIdDownloadUrlGet({
          path: { dataset_id: datasetId, audio_id: audioId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to get download URL'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
    staleTime: 1000 * 60 * 14, // URLs valid for 15 min, refetch at 14
  });
}

/**
 * Get download URL for an audio file (mutation-based, on-demand).
 */
export function useGetDownloadUrlMutation(datasetId: string) {
  return useMutation({
    mutationFn: async (audioId: string) => {
      const response =
        await getDownloadUrlApiV1DatasetsDatasetIdAudioAudioIdDownloadUrlGet({
          path: { dataset_id: datasetId, audio_id: audioId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to get download URL'
        );
      }
      return response.data;
    },
  });
}
